import React, { useEffect, useState } from 'react';
import { PortalPaymentsService } from '../../services/portal/portalPaymentsService';
import { useClientPortal } from '../../context/ClientPortalContext';
import InvoiceCard from '../../components/client/payments/InvoiceCard';
import { CurrencyDollarIcon, CheckCircleIcon, ClockIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const ClientPaymentsPage = () => {
  const { clientId } = useClientPortal();
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    if (!clientId) return;
    setLoading(true);
    const [invoiceRes, paymentRes] = await Promise.all([
      PortalPaymentsService.listInvoices(clientId),
      PortalPaymentsService.listPayments(clientId)
    ]);
    setInvoices(invoiceRes.data || []);
    setPayments(paymentRes.data || []);
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [clientId]);

  // Calculate summary stats
  const totalPaid = payments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const totalOutstanding = invoices
    .filter(i => i.status?.toLowerCase() !== 'paid')
    .reduce((sum, i) => sum + Number(i.amount_due || i.amount || 0), 0);
  const pendingInvoices = invoices.filter(i => i.status?.toLowerCase() !== 'paid');
  const paidInvoices = invoices.filter(i => i.status?.toLowerCase() === 'paid');

  const formatAmount = (amount, currency = 'USD') => {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
    } catch {
      return `${currency} ${amount.toFixed(2)}`;
    }
  };

  const handlePay = (invoice) => {
    // Could open payment modal or redirect to payment page
    alert(`Payment functionality for invoice ${invoice.invoice_number} - integrate with payment gateway`);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-32 bg-slate-100 rounded-2xl animate-pulse"></div>
        <div className="grid gap-4 sm:grid-cols-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-slate-100 rounded-2xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Payments & Invoices</h1>
            <p className="text-indigo-100 mt-1 text-sm">
              Track your payments and outstanding invoices
            </p>
          </div>
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-xl text-sm font-medium transition-colors"
          >
            <ArrowPathIcon className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-600">
              <CheckCircleIcon className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Total Paid</p>
              <p className="text-xl font-bold text-slate-900">{formatAmount(totalPaid)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-amber-100 flex items-center justify-center text-amber-600">
              <ClockIcon className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Outstanding</p>
              <p className="text-xl font-bold text-slate-900">{formatAmount(totalOutstanding)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-indigo-100 flex items-center justify-center text-indigo-600">
              <CurrencyDollarIcon className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">Pending Invoices</p>
              <p className="text-xl font-bold text-slate-900">{pendingInvoices.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Invoices */}
      {pendingInvoices.length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <ClockIcon className="h-5 w-5 text-amber-500" />
            Action Required
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {pendingInvoices.map(invoice => (
              <InvoiceCard
                key={invoice.id}
                invoice={invoice}
                onPay={handlePay}
                onDownload={() => { }}
              />
            ))}
          </div>
        </section>
      )}

      {/* Paid Invoices / Payment History */}
      <section className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="p-5 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5 text-emerald-500" />
            Payment History
          </h2>
        </div>

        {payments.length === 0 && paidInvoices.length === 0 ? (
          <div className="p-8 text-center">
            <div className="h-16 w-16 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
              <CurrencyDollarIcon className="h-8 w-8 text-slate-400" />
            </div>
            <p className="text-slate-500">No payment history yet</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {/* Show payments */}
            {payments.map(payment => (
              <div key={payment.id} className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-600">
                    <CheckCircleIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">
                      {payment.reference || `Payment #${payment.id.slice(0, 6)}`}
                    </p>
                    <p className="text-xs text-slate-500">
                      {payment.method || 'Bank transfer'} • {payment.paid_at ? new Date(payment.paid_at).toLocaleDateString() : 'Pending'}
                    </p>
                  </div>
                </div>
                <p className="text-sm font-semibold text-emerald-600">
                  {formatAmount(Number(payment.amount || 0), payment.currency)}
                </p>
              </div>
            ))}

            {/* Show paid invoices */}
            {paidInvoices.map(invoice => (
              <div key={invoice.id} className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-600">
                    <CheckCircleIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">
                      Invoice #{invoice.invoice_number || invoice.id.slice(0, 6)}
                    </p>
                    <p className="text-xs text-slate-500">
                      Paid • {invoice.paid_at ? new Date(invoice.paid_at).toLocaleDateString() : new Date(invoice.updated_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <p className="text-sm font-semibold text-emerald-600">
                  {formatAmount(Number(invoice.amount_due || invoice.amount || 0), invoice.currency)}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default ClientPaymentsPage;
